import {
  USER_TYPES,
  generalAdabyronBehaviour,
} from '../../utils/adaByron';
import FrontSonda from '@gcloud-npm/utils.monitoring.front-sonda';
import {
  MethodType,
  UnknownObject,
  Context,
} from '../../utils/types';
import {
  normalizeGetMyProductsResponse,
  normalizeGetMyLinesResponse,
} from './normalizers';

export enum R2RTYPES {
  INCLUDED = 'incluido',
  ADDITIONAL = 'adicional',
}

export enum PATHS {
  GET_MY_PRODUCTS = 'productos/misproductos',
  GET_SMART_WIFI_STATUS = 'productos/serviciosterceros/smartwifi/estado',
  GET_DEVICES_CONNECTED_SMART_WIFI = 'productos/serviciosterceros/smartwifi/dispositivos',
  GET_SECURE_CONNECTION_STATUS = 'productos/serviciosterceros/cxsegura/estado',
  GET_GAMEPASS_STATUS = 'productos/serviciosterceros/estado',
  GET_MOBILE_CONFIGURATION_STATUS = 'servicios/gestionlinea/estado',
  CHANGE_MOBILE_CONFIGURATION = 'servicios/gestionlinea/updateGestionLinea',
  GET_MOBILE_RESTRICTIONS_STATUS = 'servicios/gestionlinea/obtenrestricciones',
  CHANGE_MOBILE_RESTRICTION = 'servicios/gestionlinea/cambiarrestriccion',
  GET_PUK_CODE = 'servicios/gestionlinea/puk',
  GET_DATA_BONUS = 'productos/bonos/datos',
  GET_PRODUCT_DETAIL = 'productos/detalle',
  GET_IS_R2R_HIRED = 'productos/misproductos/renttorent',
  GET_ACTIVATE_LINE = 'productos/misproductos/activarlinea/opciones',
  GET_UPGRADES_LINES = 'productos/misproductos/mejorarlinea/opciones',
  GET_SEGUNDA_FIBRA_INFO = 'productos/misproductos/segundafibra/info',
  GET_SEGUNDA_FIBRA_ACTIVATE = 'productos/misproductos/segundafibra/activar',
  GET_SEGUNDA_FIBRA_DESACTIVATE = 'productos/misproductos/segundafibra/desactivar',
  GET_FTTR_AVAILABILITY = 'productos/misproductos/fibrainvisible',
}

export const getMyProducts = (
  data: { catalogSubSections?: string[]; withoutCache?: boolean },
  context: Context,
  idFront = 'VT',
  sendSondaTracker = '',
): Promise<unknown> => {
  const { catalogSubSections, withoutCache } = data;

  let queryParams = [];
  if (Array.isArray(catalogSubSections) && catalogSubSections.length > 0) {
    queryParams.push({
      param: 'subsecciones',
      value: catalogSubSections.toString(),
    });
  }

  if (withoutCache) {
    queryParams = [...queryParams, { param: 'nocache' }];
  }

  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_MY_PRODUCTS,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES],
    eventName: 'PRODUCTS.MY_PRODUCTS',
    queryParams,
    mockedInfo: 'getMyProducts',
    methodType: MethodType.GET,
    onSuccessCallback: (xhr: XMLHttpRequest): UnknownObject[] => {
      const { response } = xhr;
      if (sendSondaTracker) {
        const frontSonda = new FrontSonda({
          processName: 'INVESTIGACION_generalAdabyronBehaviour',
          context,
          filePath: 'ADABYRON:getMyProducts',
        } as any);
        frontSonda.warningMessage({
          info: `CASO-PARTICULAR-ADABYRON: responseRaw:${JSON.stringify({
            response,
          })}  tracker:${JSON.stringify({
            sendSondaTracker,
          })}
          `,
        });
      }
      const { datos } = JSON.parse(response);
      return normalizeGetMyProductsResponse(datos.productosContratados);
    },
  });
};

export const getMyLines = (
  context: Context,
  idFront = 'VT',
  allowRecentMobileLines = true,
  retryAllMobileLines = false,
): Promise<unknown> => {
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_MY_PRODUCTS,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCTS.MY_PRODUCTS',
    mockedInfo: 'getMyLines',
    methodType: MethodType.GET,
    onSuccessCallback: (xhr: XMLHttpRequest): UnknownObject => {
      const { response } = xhr;
      const { datos } = JSON.parse(response);
      return normalizeGetMyLinesResponse(
        datos.productosContratados,
        allowRecentMobileLines,
        retryAllMobileLines,
      );
    },
  });
};

export const getSmartWiFiStatus = (
  data: { phone: string },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { phone } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_SMART_WIFI_STATUS,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCTS.GET_SMART_WIFI_STATUS',
    mockedInfo: 'getSmartWiFiStatus',
    methodType: MethodType.POST,
    requestObject: {
      telefono: phone,
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getDevicesConnectedSmartWiFi = (
  data: { phone: string },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { phone } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_DEVICES_CONNECTED_SMART_WIFI,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCTS.GET_DEVICES_CONNECTED_SMART_WIFI',
    mockedInfo: 'getDevicesConnectedSmartWiFi',
    methodType: MethodType.POST,
    requestObject: {
      telefono: phone,
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getSecureConnectionStatus = (
  data: { phone: string },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { phone } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_SECURE_CONNECTION_STATUS,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCTS.GET_SECURE_CONNECTION_STATUS',
    mockedInfo: 'getSecureConnectionStatus',
    methodType: MethodType.POST,
    requestObject: {
      telefono: phone,
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getMobileConfigurationStatus = (
  data: { phone: string, type?: string },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { phone, type } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_MOBILE_CONFIGURATION_STATUS,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCTS.GET_MOBILE_CONFIGURATION_STATUS',
    mockedInfo: 'getMobileConfigurationStatus',
    methodType: MethodType.POST,
    requestObject: {
      telefono: phone,
      type: type,
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const changeMobileConfiguration = (
  data: {
    phone: string;
    serviceType: string;
    status: string;
    diversionType: string;
    diversionPhone: string;
    svaId: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const {
    phone,
    serviceType,
    status,
    diversionType,
    diversionPhone,
    svaId,
  } = data;

  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.CHANGE_MOBILE_CONFIGURATION,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCTS.CHANGE_MOBILE_CONFIGURATION',
    mockedInfo: 'changeMobileConfiguration',
    methodType: MethodType.POST,
    requestObject: {
      telefono: phone,
      tipo_accion: status,
      tipo_servicio: serviceType,
      tipo_desvio: diversionType,
      telefono_desvio: diversionPhone,
      sva: svaId,
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getMobileRestrictionsStatus = (
  data: {
    productId: string;
    phoneType: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { productId, phoneType } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_MOBILE_RESTRICTIONS_STATUS,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCTS.GET_MOBILE_RESTRICTIONS_STATUS',
    queryParams: [
      {
        param: 'productId',
        value: productId,
      },
      { param: 'type', value: phoneType },
    ],
    mockedInfo: 'getMobileRestrictionsStatus',
    methodType: MethodType.GET,
  });
};

export const changeMobileRestriction = (
  data: {
    productId: string;
    restriction: string;
    action: string;
    id: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { productId, restriction, action, id } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.CHANGE_MOBILE_RESTRICTION,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCTS.CHANGE_MOBILE_RESTRICTION',
    mockedInfo: 'changeMobileRestriction',
    methodType: MethodType.POST,
    requestObject: {
      productId,
      restriccion: restriction,
      accion: action,
      id,
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getPukCode = (
  data: {
    productId: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { productId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_PUK_CODE,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCTS.GET_PUK_CODE',
    queryParams: [
      {
        param: 'productId',
        value: productId,
      },
    ],
    mockedInfo: 'getPukCode',
    methodType: MethodType.GET,
  });
};

export const getDataBonus = (
  data: { productId: string },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { productId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_DATA_BONUS,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES],
    eventName: 'PRODUCT.GET_DATA_BONUS',
    queryParams: [
      {
        param: 'productId',
        value: productId,
      },
    ],
    mockedInfo: 'getDataBonus',
    methodType: MethodType.GET,
  });
};

export const getProductDetail = (
  data: {
    bundle?: string;
    productId?: string;
    productOfferingId?: string;
    type?: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { bundle, productId, productOfferingId, type } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_PRODUCT_DETAIL,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES],
    eventName: 'PRODUCT.GET_PRODUCT_DETAIL',
    queryParams: [
      {
        param: 'bundle',
        value: bundle,
      },
      { param: 'productId', value: productId },
      { param: 'productOfferingId', value: productOfferingId },
      { param: 'productType', value: type },
    ],
    mockedInfo: 'getProductDetail',
    methodType: MethodType.GET,
  });
};

export const getIsR2rHired = (
  data: {
    type?: R2RTYPES;
    productOffering?: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { type, productOffering } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_IS_R2R_HIRED,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCT.GET_IS_R2R_HIRED',
    mockedInfo: 'getIsR2rHired',
    methodType: MethodType.GET,
    queryParams: [
      {
        param: 'type',
        value: type,
      },
      {
        param: 'productOfferingConvergente',
        value: productOffering,
      },
    ],
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getActivateLine = (
  context: Context,
  idFront = 'VT',
  ): Promise<unknown> => {
    return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_ACTIVATE_LINE,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCTS.GET_ACTIVATE_LINE',
    mockedInfo: 'getActivateLine',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getUpgradesLines = (
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_UPGRADES_LINES,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCTS.GET_UPGRADES_LINES',
    mockedInfo: 'getUpgradesLines',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getGamePassStatus = (
  data: {
    idProducto: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { idProducto } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_GAMEPASS_STATUS,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCTS.GET_GAMEPASS_STATUS',
    queryParams: [
      {
        param: 'idProducto',
        value: idProducto,
      },
    ],
    mockedInfo: 'getGamePassStatus',
    methodType: MethodType.GET,
  });
};

export const getSegundaFibraInfo = (
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_SEGUNDA_FIBRA_INFO,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCTS.GET_SEGUNDA_FIBRA_INFO',
    mockedInfo: 'getSegundaFibraInfo',
    methodType: MethodType.GET,
  });
};

export const postActivateSegundaFibra = (
  data: {
    productId;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { productId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_SEGUNDA_FIBRA_ACTIVATE,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCTS.GET_SEGUNDA_FIBRA_ACTIVATE',
    mockedInfo: 'postActivateSegundaFibra',
    methodType: MethodType.POST,
    requestObject: {
      productId,
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const postDesactivateSegundaFibra = (
  data: {
    productId;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { productId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_SEGUNDA_FIBRA_DESACTIVATE,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCTS.GET_SEGUNDA_FIBRA_DESACTIVATE',
    mockedInfo: 'postDesactivateSegundaFibra',
    methodType: MethodType.POST,
    requestObject: {
      productId,
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getFttrAvailability = (
  data: {
    productOfferingId: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { productOfferingId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_FTTR_AVAILABILITY,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PRODUCT.GET_FTTR_AVAILABILITY',
    mockedInfo: 'getFttrAvailability',
    methodType: MethodType.GET,
    queryParams: [
      {
        param: 'productOfferingId',
        value: productOfferingId,
      },
    ],
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};
